/**
 * Recently opened repositories (Phase 4P).
 *
 * Kept in this browser only. There is no route for a per-account recents list
 * and this is the kind of convenience that belongs to a browser anyway: it is
 * what you were just looking at, here. Storage can be absent or refuse
 * (private windows, cleared data), so every read and write is guarded and the
 * list simply stays empty.
 */

import { useCallback, useEffect, useState } from 'react';

export interface RecentRepository {
  orgId: string;
  repoId: string;
  name: string;
  /** The organization's display name, when the page knew it. */
  org?: string;
  openedAt: string;
}

const KEY = 'exigit.recent-repositories';
const LIMIT = 8;
const EVENT = 'exigit:recent-repositories';

export function readRecent(): RecentRepository[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is RecentRepository =>
        typeof r === 'object' &&
        r !== null &&
        typeof (r as RecentRepository).orgId === 'string' &&
        typeof (r as RecentRepository).repoId === 'string' &&
        typeof (r as RecentRepository).name === 'string',
    );
  } catch {
    return [];
  }
}

export function noteRecent(entry: Omit<RecentRepository, 'openedAt'>): void {
  const next: RecentRepository[] = [
    { ...entry, openedAt: new Date().toISOString() },
    ...readRecent().filter((r) => !(r.orgId === entry.orgId && r.repoId === entry.repoId)),
  ].slice(0, LIMIT);
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // Storage refused; the list is a convenience.
  }
}

export function clearRecent(): void {
  try {
    window.localStorage.removeItem(KEY);
    window.dispatchEvent(new Event(EVENT));
  } catch {
    // As above.
  }
}

/** The list, re-read whenever this or another tab changes it. */
export function useRecentRepositories(): RecentRepository[] {
  const [recent, setRecent] = useState<RecentRepository[]>(() => readRecent());
  const refresh = useCallback(() => {
    setRecent(readRecent());
  }, []);
  useEffect(() => {
    window.addEventListener(EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);
  return recent;
}
